import * as React from "react";
import { Collapse, Fab, Typography, makeStyles } from "@material-ui/core";
import { MoreVert } from "@material-ui/icons";
import * as qs from "query-string";
import { useTranslation } from "react-i18next";
import { Link } from "src/common/components";
import { IconArrowDropDown } from "src/common/icons";
import {
  IAppReduxState,
  ICycleConfig,
  ITalk,
  IUser,
  ITalkQuestionnaire,
  OpenTasksSubmissionStatus,
  PeerSelectionStatus,
  QuestionnaireType,
  SubmissionStatus,
  TalkStatus,
  ISubmitSessionDatePayload
} from "src/common/types";
import { dateWithoutTime, displayDate } from "src/helpers";
import DateStatus from "../DateStatus";
import EmployeeCardOptions from "./EmployeeCardOptions";
import ScheduleTalkDateForm from "./scheduleTalkDateForm";
import Status from "./status";
import { useSelector, shallowEqual } from "react-redux";

const useStyles = makeStyles((theme: any) => ({
  arrow: {
    display: "inline-block" as "inline-block",
    fontSize: "1.35rem",
    lineHeight: "1",
    transform: "rotate(360deg) scale(0.9,1.35)",
    transition: "transform 0.2s linear",
    verticalAlign: "middle" as "middle"
  },
  cycleAction: {
    "& $expandWrapper": {
      display: "none" as "none",
      [theme.breakpoints.up("md")]: {
        display: "block" as "block"
      }
    },
    "&:last-child": {
      borderBottom: "none" as "none"
    },
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    flexWrap: "wrap" as "wrap",
    justifyContent: "space-between" as "space-between",
    lineHeight: "30px",
    [theme.breakpoints.up("md")]: {
      flexWrap: "nowrap" as "nowrap",
      justifyContent: "flex-start" as "flex-start",
      lineHeight: "60px"
    }
  },
  cycleActionName: {
    display: "block" as "block",
    flexGrow: 1,
    padding: "0 16px",
    width: "100%",
    [theme.breakpoints.up("md")]: {
      padding: "0",
      width: "auto" as "auto"
    }
  },
  cycleActions: {
    padding: "0",
    transition: ".3s max-height ease-out",
    [theme.breakpoints.up("md")]: {
      padding: "0 0 0 66px"
    }
  },
  cycleDetails: {
    "& $expandWrapper": {
      order: 2,
      [theme.breakpoints.up("md")]: {
        order: "initial" as "initial"
      }
    },
    "& $mobileDates": {
      maxWidth: "50%",
      order: 3,
      [theme.breakpoints.up("md")]: {
        maxWidth: "none" as "none",
        order: "initial" as "initial"
      }
    },
    "& $statusWrapper": {
      maxWidth: "50%",
      order: 4,
      [theme.breakpoints.up("md")]: {
        maxWidth: "none" as "none",
        order: "initial" as "initial"
      }
    },
    borderBottom: "1px solid #e6e6e6",
    display: "flex" as "flex",
    flexWrap: "wrap" as "wrap",
    justifyContent: "space-between" as "space-between",
    lineHeight: "30px",
    [theme.breakpoints.up("md")]: {
      flexWrap: "nowrap" as "nowrap",
      justifyContent: "initial" as "initial",
      lineHeight: "60px"
    }
  },
  cycleDetailsName: {
    display: "block" as "block",
    flexGrow: 1,
    padding: "0 8px 0 16px",
    width: "calc( 100% - 94px)",
    [theme.breakpoints.up("md")]: {
      width: "auto" as "auto"
    }
  },
  dateWrapper: {
    color: "#707070",
    display: "none" as "none",
    minWidth: 110,
    padding: "0 8px",
    width: 110,
    [theme.breakpoints.up("md")]: {
      display: "block" as "block"
    }
  },
  expandButtonLabel: {
    height: "100%"
  },
  expandLabel: {
    alignItems: "center" as "center",
    cursor: "pointer" as "pointer",
    display: "flex" as "flex",
    height: "40px",
    justifyContent: "center" as "center",
    width: "40px"
  },
  expandWrapper: {
    minWidth: 54,
    padding: "0 16px 0 8px",
    width: 54
  },
  fab: {
    background: "none" as "none",
    boxShadow: "none" as "none"
  },
  mobileDates: {
    display: "block" as "block",
    fontSize: "1.2rem",
    padding: "0 8px 0 16px",
    [theme.breakpoints.up("md")]: {
      display: "none" as "none"
    }
  },
  open: {
    "& $arrow": {
      transform: "rotate(180deg) scale(0.9,1.35)",
      transition: "transform 0.2s linear"
    },
    "& $cycleActions": {
      borderBottom: "1px solid #e6e6e6"
    }
  },
  optionsWrapper: {
    display: "block" as "block",
    minWidth: 40,
    position: "relative" as "relative",
    width: 40,
    [theme.breakpoints.up("md")]: {
      display: "block" as "block"
    }
  },
  peerSummaryCount: {
    paddingLeft: 5
  },
  scheduleDatePlainTextWrapper: {
    color: "#707070",
    display: "none" as "none",
    minWidth: 154,
    width: 154,
    [theme.breakpoints.up("md")]: {
      display: "flex"
    }
  },
  scheduleDateWrapper: {
    [theme.breakpoints.up("md")]: {
      alignItems: "center",
      display: "flex",
      minWidth: 154
    },
    display: "none"
  },
  statusWrapper: {
    alignItems: "center",
    display: "flex",
    padding: "0px 8px",
    [theme.breakpoints.up("md")]: {
      minWidth: 184,
      width: 184
    }
  },
  title: {
    display: "inline-block" as "inline-block",
    lineHeight: 1,
    overflowWrap: "break-word" as "break-word",
    verticalAlign: "middle" as "middle",
    wordBreak: "break-word" as "break-word"
  }
}));

interface IProps extends ITalk {
  icName: string;
  icFullName: string;
  icEmail?: string | null;
  dsvName: string;
  onCancellation: (talkId: string) => void;
  onReassign: (talkId: string, cycleId: string) => void;
  onReopen: (talkId: string) => void;
  onScheduleTalkDateSubmit: (
    talkId: string,
    data: ISubmitSessionDatePayload,
    icName: string,
    icEmail?: string | null
  ) => void;
  defaultCollapsed: boolean;
}
interface IStateProps {
  currentUser: IUser;
}

const mapStateToProps = ({ currentUser: { userData } }: IAppReduxState): IStateProps => ({
  currentUser: userData as IUser
});

const EmployeeCardTalk = ({
  id: talkId,
  cycle: { id: cycleId, name: cycleName, config: cycleConfig },
  status: talkStatus,
  scheduledDate,
  talkCompletionDueDate,
  talkQuestionnaires,
  participantSignedDate,
  supervisorSignedDate,
  peerSelection,
  icFullName,
  icEmail,
  onScheduleTalkDateSubmit,
  onCancellation,
  onReassign,
  onReopen,
  icName,
  dsvName,
  defaultCollapsed,
  peerSummaryCount
}: IProps) => {
  const {
    currentUser: { id: currentUserId }
  } = useSelector(mapStateToProps, shallowEqual) as IStateProps;
  const classes = useStyles();

  const handleScheduleTalkDate = React.useCallback(
    (data: ISubmitSessionDatePayload) => {
      onScheduleTalkDateSubmit(talkId, data, icName, icEmail);
    },
    [talkId]
  );
  const currentDate = React.useMemo(() => dateWithoutTime(new Date()), []);

  // Expand talk if it's not completed
  const defaultOpen = React.useMemo(() => {
    return defaultCollapsed ? false : ![TalkStatus.Done, TalkStatus.Cancelled].includes(talkStatus);
  }, [talkStatus, defaultCollapsed]);

  const [open, updateOpen] = React.useState(defaultOpen);
  const [optionsOpen, updateOptionsOpen] = React.useState(false);
  const handleOptionsClose = React.useCallback(() => {
    updateOptionsOpen(false);
  }, []);
  const handleOptionsOpen = React.useCallback(() => {
    updateOptionsOpen(true);
  }, []);

  const {
    t,
    i18n: { language: lng }
  } = useTranslation();

  /**
   * Shows a counter with how many questionnaires have been submitted so far
   */
  const getPeerSummaryCount = React.useMemo(
    () =>
      peerSummaryCount ? (
        <span className={classes.peerSummaryCount}>
          {`(${peerSummaryCount.submittedPeerQuestionnairesCount}/${peerSummaryCount.allPeerQuestionnairesCount})`}
        </span>
      ) : undefined,
    [peerSummaryCount]
  );

  const toggleOpen = React.useCallback(() => {
    updateOpen(!open);
  }, [open]);

  const TalkSheetAction = React.useMemo(() => {
    const supervisorQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.DirectSupervisor
    );

    const selfAssessmentQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.SelfAssessment
    );

    const completionDatePassed = currentDate > dateWithoutTime(talkCompletionDueDate);

    const talkSignedDate =
      participantSignedDate && supervisorSignedDate
        ? [participantSignedDate, supervisorSignedDate].sort((date1, date2) => date2.getTime() - date1.getTime())[0]
        : null;

    const talkDateStatus =
      completionDatePassed && OpenTasksSubmissionStatus.includes(talkStatus)
        ? "warning"
        : completionDatePassed && talkStatus === TalkStatus.Done
        ? "success"
        : "normal";

    const enableScheduleTalk =
      ![TalkStatus.Cancelled].includes(talkStatus) && participantSignedDate === null && supervisorSignedDate === null;

    const thisCycleConfig = cycleConfig as ICycleConfig;
    const showOptions =
      thisCycleConfig.showCancelTalkButton ||
      thisCycleConfig.showReassignSupervisorButton ||
      thisCycleConfig.showReopenTalkButton;

    const questionnaireTemplateConfig =
      (supervisorQuestionnaire && supervisorQuestionnaire.questionnaireTemplateConfig) ||
      (selfAssessmentQuestionnaire && selfAssessmentQuestionnaire.questionnaireTemplateConfig) ||
      null;

    const { supportedLanguages = null } = questionnaireTemplateConfig ? JSON.parse(questionnaireTemplateConfig) : {};
    return (
      <div className={classes.cycleDetails}>
        <div className={classes.cycleDetailsName}>
          <Link
            to={`/${lng}/dashboard/my-employees/talk-sheet/${talkId}?${btoa(
              qs.stringify(
                { supported: [supportedLanguages] },
                {
                  arrayFormat: "comma"
                }
              )
            )}`}
          >
            <Typography className={classes.title} display="inline" color="primary" variant="subtitle2">
              {cycleName}
            </Typography>
          </Link>
        </div>
        <div className={classes.mobileDates}>
          {talkStatus === TalkStatus.Done ? (
            <>
              {`${displayDate(talkSignedDate as Date)} / `}
              <DateStatus type={talkDateStatus} date={talkCompletionDueDate} />
            </>
          ) : (
            <>
              {`${t(`feedback.page.my-employees.table.dueDate`)}: `}
              <DateStatus type={talkDateStatus} date={talkCompletionDueDate} />
            </>
          )}
        </div>
        <div className={classes.statusWrapper}>
          <Status icName={icName} dsvName={dsvName} type="talk" status={talkStatus} />
        </div>
        <div className={classes.scheduleDateWrapper}>
          {enableScheduleTalk ? (
            <ScheduleTalkDateForm
              initialValues={{
                scheduledDate
              }}
              onSubmit={handleScheduleTalkDate}
            />
          ) : scheduledDate ? (
            <div className={classes.scheduleDatePlainTextWrapper}>
              <DateStatus type="normal" date={scheduledDate} />
            </div>
          ) : null}
        </div>
        <div className={classes.dateWrapper}>{talkSignedDate && displayDate(talkSignedDate)}</div>
        <div className={classes.dateWrapper}>
          <DateStatus type={talkDateStatus} date={talkCompletionDueDate} />
        </div>
        <div className={classes.optionsWrapper}>
          {showOptions ? (
            <>
              <Fab onClick={handleOptionsOpen} className={classes.fab} size="small">
                <MoreVert color="primary" />
              </Fab>
              <EmployeeCardOptions
                onCancel={onCancellation}
                onReassign={onReassign}
                open={optionsOpen}
                onClose={handleOptionsClose}
                talkId={talkId}
                cycleId={cycleId}
                showReasign={thisCycleConfig.showReassignSupervisorButton}
                reassignDisabled={[TalkStatus.Done, TalkStatus.Cancelled].includes(talkStatus)}
                showCancel={thisCycleConfig.showCancelTalkButton}
                showReopen={thisCycleConfig.showReopenTalkButton}
                onReopen={onReopen}
                reopenDisabled={talkStatus !== TalkStatus.Cancelled}
                cancelDisabled={[TalkStatus.Done, TalkStatus.Cancelled].includes(talkStatus)}
              />
            </>
          ) : null}
        </div>
        <div className={classes.expandWrapper}>
          <Fab
            classes={{ label: classes.expandButtonLabel }}
            className={classes.fab}
            onClick={toggleOpen}
            size="small"
            color="default"
          >
            <label className={classes.expandLabel}>
              <Typography color="primary" className={classes.arrow}>
                <IconArrowDropDown />
              </Typography>
            </label>
          </Fab>
        </div>
      </div>
    );
  }, [talkQuestionnaires, lng, toggleOpen, optionsOpen, onCancellation, talkStatus, cycleConfig]);

  const DSVFeedbackAction = React.useMemo(() => {
    const supervisorQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.DirectSupervisor
    );
    if (supervisorQuestionnaire) {
      const {
        id: questionnaireId,
        submissionDate: supervisorSubmissionDate,
        submissionDueDate: supervisorSubmissionDueDate,
        submissionStatus: supervisorSubmissionStatus
      } = supervisorQuestionnaire;
      const isCurrentUserDSV = supervisorQuestionnaire.feedbackGiver.id === currentUserId;
      const supervisorSubmissionDatePassed = currentDate > dateWithoutTime(supervisorSubmissionDueDate);

      const supervisorDateStatus =
        supervisorSubmissionDatePassed && OpenTasksSubmissionStatus.includes(supervisorSubmissionStatus)
          ? "warning"
          : supervisorSubmissionDatePassed && supervisorSubmissionStatus === SubmissionStatus.Submitted
          ? "success"
          : "normal";

      const { supportedLanguages = null } = supervisorQuestionnaire.questionnaireTemplateConfig
        ? JSON.parse(supervisorQuestionnaire.questionnaireTemplateConfig)
        : {};

      return (
        <div className={classes.cycleAction}>
          <div className={classes.cycleActionName}>
            {![SubmissionStatus.Cancelled].includes(supervisorSubmissionStatus) && isCurrentUserDSV ? (
              <Link
                to={`/${lng}/dashboard/my-employees/my-questionnaire/${questionnaireId}?${btoa(
                  qs.stringify(
                    { supported: [supportedLanguages] },
                    {
                      arrayFormat: "comma"
                    }
                  )
                )}`}
              >
                {t(`feedback.receiver.directSupervisor`, {
                  name: icFullName
                })}
              </Link>
            ) : (
              <Typography className={classes.title} display="inline" variant="subtitle1">
                {t(`feedback.receiver.directSupervisor`, {
                  name: icFullName
                })}
              </Typography>
            )}
          </div>
          <div className={classes.mobileDates}>
            {supervisorSubmissionStatus === SubmissionStatus.Submitted ? (
              <>
                {`${displayDate(supervisorSubmissionDate as Date)} / `}
                <DateStatus type={supervisorDateStatus} date={supervisorSubmissionDueDate} />
              </>
            ) : (
              <>
                {`${t(`feedback.page.my-employees.table.dueDate`)}: `}
                <DateStatus type={supervisorDateStatus} date={supervisorSubmissionDueDate} />
              </>
            )}
          </div>
          <div className={classes.statusWrapper}>
            <Status type="questionnaire" status={supervisorSubmissionStatus} />
          </div>
          <div className={classes.scheduleDateWrapper} />
          <div className={classes.dateWrapper}>
            {supervisorSubmissionDate && displayDate(supervisorSubmissionDate as Date)}
          </div>
          <div className={classes.dateWrapper}>
            <DateStatus type={supervisorDateStatus} date={supervisorSubmissionDueDate as Date} />
          </div>
          <div className={classes.optionsWrapper} />
          <div className={classes.expandWrapper} />
        </div>
      );
    } else {
      return null;
    }
  }, [talkQuestionnaires, lng]);

  const ParticipantAction = React.useMemo(() => {
    const participantQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.SelfAssessment
    ) as ITalkQuestionnaire;

    if (participantQuestionnaire) {
      const {
        submissionDate: participantSubmissionDate,
        submissionDueDate: participantSubmissionDueDate,
        submissionStatus: participantSubmissionStatus
      } = participantQuestionnaire;

      const participantSubmissionDatePassed = currentDate > dateWithoutTime(participantSubmissionDueDate);

      const participantDateStatus =
        participantSubmissionDatePassed && OpenTasksSubmissionStatus.includes(participantSubmissionStatus)
          ? "warning"
          : participantSubmissionDatePassed && participantSubmissionStatus === SubmissionStatus.Submitted
          ? "success"
          : "normal";

      return (
        <div className={classes.cycleAction}>
          <div className={classes.cycleActionName}>
            <Typography className={classes.title} display="inline" variant="subtitle1">
              {t(`feedback.submitter.selfAssessment.receiverView`, {
                name: icName
              })}
            </Typography>
          </div>
          <div className={classes.mobileDates}>
            {participantSubmissionStatus === SubmissionStatus.Submitted ? (
              <>
                {`${displayDate(participantSubmissionDate as Date)} / `}
                <DateStatus type={participantDateStatus} date={participantSubmissionDueDate} />
              </>
            ) : (
              <>
                {`${t(`feedback.page.my-employees.table.dueDate`)}: `}
                <DateStatus type={participantDateStatus} date={participantSubmissionDueDate as Date} />
              </>
            )}
          </div>
          <div className={classes.statusWrapper}>
            <Status type="questionnaire" status={participantSubmissionStatus} />
          </div>
          <div className={classes.scheduleDateWrapper} />
          <div className={classes.dateWrapper}>
            {participantSubmissionDate && displayDate(participantSubmissionDate)}
          </div>
          <div className={classes.dateWrapper}>
            <DateStatus type={participantDateStatus} date={participantSubmissionDueDate} />
          </div>
          <div className={classes.optionsWrapper} />
          <div className={classes.expandWrapper} />
        </div>
      );
    } else {
      return null;
    }
  }, [talkQuestionnaires, lng]);
  /**
   * Calculates if the Peer feedback link should be disabled.
   * If the peerselection status is not submitted and the duedate has passsed
   * then the link should be disabled.
   */
  const getPeerSelectionLinkStatus = React.useCallback(
    (peerSelectionStatus: PeerSelectionStatus) => {
      if (
        [PeerSelectionStatus.Cancelled].includes(peerSelectionStatus) ||
        [PeerSelectionStatus.Submitted].includes(peerSelectionStatus)
      ) {
        return (
          <Typography className={classes.title} display="inline" variant="subtitle1">
            {t(`feedback.receiver.peerSelection`, {
              name: icFullName
            })}
          </Typography>
        );
      } else {
        return (
          <Link to={`/${lng}/dashboard/my-employees/peer-selection/${talkId}`}>
            {t(`feedback.receiver.peerSelection`, {
              name: icFullName
            })}
          </Link>
        );
      }
    },
    [peerSelection]
  );

  const PeerSelectionAction = React.useMemo(() => {
    if (peerSelection) {
      const {
        dueDate: peerSelectionDueDate,
        status: peerSelectionStatus,
        submissionDate: peerSelectionSubmissionDate
      } = peerSelection;

      const peerSelectionPassed = currentDate > dateWithoutTime(peerSelectionDueDate);

      const peerStatus =
        peerSelectionPassed && OpenTasksSubmissionStatus.includes(peerSelectionStatus)
          ? "warning"
          : peerSelectionPassed && peerSelectionStatus === PeerSelectionStatus.Submitted
          ? "success"
          : "normal";

      return (
        <div className={classes.cycleAction}>
          <div className={classes.cycleActionName}>{getPeerSelectionLinkStatus(peerSelectionStatus)}</div>
          <div className={classes.mobileDates}>
            {peerSelectionStatus === PeerSelectionStatus.Submitted ? (
              <>
                {`${displayDate(peerSelectionSubmissionDate as Date)} / `}
                <DateStatus type={peerStatus} date={peerSelectionDueDate} />
              </>
            ) : (
              <>
                {`${t(`feedback.page.my-employees.table.dueDate`)}: `}
                <DateStatus type={peerStatus} date={peerSelectionDueDate as Date} />
              </>
            )}
          </div>
          <div className={classes.statusWrapper}>
            <Status type="peerSelection" status={peerSelectionStatus} />
          </div>
          <div className={classes.scheduleDateWrapper} />
          <div className={classes.dateWrapper}>
            {peerSelectionSubmissionDate && displayDate(peerSelectionSubmissionDate)}
          </div>
          <div className={classes.dateWrapper}>
            <DateStatus type={peerStatus} date={peerSelectionDueDate} />
          </div>
          <div className={classes.optionsWrapper} />
          <div className={classes.expandWrapper} />
        </div>
      );
    } else {
      return null;
    }
  }, [peerSelection, lng]);

  const PeerSummaryAction = React.useMemo(() => {
    const peerSummaryQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.PeerSummary
    ) as ITalkQuestionnaire;

    if (peerSummaryQuestionnaire) {
      const isCurrentUserDSV = peerSummaryQuestionnaire.feedbackGiver.id === currentUserId;

      const {
        submissionDate: peerSummarySubmissionDate,
        submissionDueDate: peerSummarySubmissionDueDate,
        submissionStatus: peerSummarySubmissionStatus
      } = peerSummaryQuestionnaire;

      const peerSummarySubmissionDatePassed = currentDate > dateWithoutTime(peerSummarySubmissionDueDate);
      const peerSummaryDateStatus =
        peerSummarySubmissionDatePassed && OpenTasksSubmissionStatus.includes(peerSummarySubmissionStatus)
          ? "warning"
          : peerSummarySubmissionDatePassed && peerSummarySubmissionStatus === SubmissionStatus.Submitted
          ? "success"
          : "normal";

      const { supportedLanguages = null } = peerSummaryQuestionnaire.questionnaireTemplateConfig
        ? JSON.parse(peerSummaryQuestionnaire.questionnaireTemplateConfig)
        : {};

      return (
        <div className={classes.cycleAction}>
          <div className={classes.cycleActionName}>
            {![SubmissionStatus.Cancelled].includes(peerSummarySubmissionStatus) && isCurrentUserDSV ? (
              <Link
                to={`/${lng}/dashboard/my-employees/peer-summary/${talkId}?${btoa(
                  qs.stringify(
                    { supported: [supportedLanguages] },
                    {
                      arrayFormat: "comma"
                    }
                  )
                )}`}
              >
                {t(`feedback.receiver.peerSummary`, {
                  name: icFullName
                })}
              </Link>
            ) : (
              <Typography className={classes.title} display="inline" variant="subtitle1">
                {t(`feedback.receiver.peerSummary`, {
                  name: icFullName
                })}
              </Typography>
            )}
          </div>
          <div className={classes.mobileDates}>
            {peerSummarySubmissionStatus === SubmissionStatus.Submitted ? (
              <>
                {`${displayDate(peerSummarySubmissionDate as Date)} / `}
                <DateStatus type={peerSummaryDateStatus} date={peerSummarySubmissionDueDate} />
              </>
            ) : (
              <>
                {`${t(`feedback.page.my-employees.table.dueDate`)}: `}
                <DateStatus type={peerSummaryDateStatus} date={peerSummarySubmissionDueDate as Date} />
              </>
            )}
          </div>
          <div className={classes.statusWrapper}>
            <Status
              type="questionnaire"
              peerSummaryCountText={getPeerSummaryCount}
              status={peerSummarySubmissionStatus}
            />
          </div>
          <div className={classes.scheduleDateWrapper} />
          <div className={classes.dateWrapper}>
            {peerSummarySubmissionDate && displayDate(peerSummarySubmissionDate)}
          </div>
          <div className={classes.dateWrapper}>
            <DateStatus type={peerSummaryDateStatus} date={peerSummarySubmissionDueDate} />
          </div>
          <div className={classes.optionsWrapper} />
          <div className={classes.expandWrapper} />
        </div>
      );
    } else {
      return null;
    }
  }, [talkQuestionnaires, lng, peerSummaryCount]);

  const AnonymousSummaryAction = React.useMemo(() => {
    let questionnaire = {} as ITalkQuestionnaire;
    let totalAnonQuestions = 0;
    let totalAnonSubmitted = 0;
    talkQuestionnaires.forEach(q => {
      if (q.questionnaireType === QuestionnaireType.AnonymousDSV) {
        totalAnonQuestions++;

        if (q.submissionStatus === SubmissionStatus.Submitted) {
          totalAnonSubmitted++;
        }
      }
    });
    const anonymousSummaryQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.AnonymousDSVSummary
    ) as ITalkQuestionnaire;
    const anonymousQuestionnaire = talkQuestionnaires.find(
      ({ questionnaireType }) => questionnaireType === QuestionnaireType.AnonymousDSV
    ) as ITalkQuestionnaire;
    questionnaire = anonymousSummaryQuestionnaire || anonymousQuestionnaire;
    if (questionnaire) {
      const {
        submissionDate: anonSummarySubmissionDate,
        submissionDueDate: anonSummarySubmissionDueDate,
        submissionStatus: anonSummarySubmissionStatus
      } = questionnaire;

      const anonSummarySubmissionDatePassed = currentDate > dateWithoutTime(anonSummarySubmissionDueDate);
      const anonSummaryDateStatus =
        anonSummarySubmissionDatePassed && OpenTasksSubmissionStatus.includes(anonSummarySubmissionStatus)
          ? "warning"
          : anonSummarySubmissionDatePassed && anonSummarySubmissionStatus === SubmissionStatus.Submitted
          ? "success"
          : "normal";

      const { supportedLanguages = null } = questionnaire.questionnaireTemplateConfig
        ? JSON.parse(questionnaire.questionnaireTemplateConfig)
        : {};

      return (
        <div className={classes.cycleAction}>
          <div className={classes.cycleActionName}>
            {![SubmissionStatus.Cancelled].includes(anonSummarySubmissionStatus) ? (
              <Link
                to={`/${lng}/dashboard/my-employees/anonymous-summary/${talkId}?${btoa(
                  qs.stringify(
                    { supported: [supportedLanguages] },
                    {
                      arrayFormat: "comma"
                    }
                  )
                )}`}
              >
                {t(`feedback.receiver.anonymous`, {
                  name: icFullName
                })}
              </Link>
            ) : (
              <Typography className={classes.title} display="inline" variant="subtitle1">
                {t(`feedback.receiver.anonymous`, {
                  name: icFullName
                })}
              </Typography>
            )}
          </div>
          <div className={classes.mobileDates}>
            {anonSummarySubmissionStatus === SubmissionStatus.Submitted ? (
              <>
                {`${displayDate(anonSummarySubmissionDate as Date)} / `}
                <DateStatus type={anonSummaryDateStatus} date={anonSummarySubmissionDueDate} />
              </>
            ) : (
              <>
                {`${t(`feedback.page.my-employees.table.dueDate`)}: `}
                <DateStatus type={anonSummaryDateStatus} date={anonSummarySubmissionDueDate as Date} />
              </>
            )}
          </div>
          <div className={classes.statusWrapper}>
            <Status
              type="questionnaire"
              status={anonSummarySubmissionStatus}
              extraText={` (${totalAnonSubmitted}/${totalAnonQuestions})`}
            />
          </div>
          <div className={classes.scheduleDateWrapper} />
          <div className={classes.dateWrapper}>
            {anonSummarySubmissionDate && displayDate(anonSummarySubmissionDate)}
          </div>
          <div className={classes.dateWrapper}>
            <DateStatus type={anonSummaryDateStatus} date={anonSummarySubmissionDueDate} />
          </div>
          <div className={classes.optionsWrapper} />
          <div className={classes.expandWrapper} />
        </div>
      );
    } else {
      return null;
    }
  }, [talkQuestionnaires, lng, peerSummaryCount]);

  const containerClassName = React.useMemo(() => {
    const classNames = [];
    if (open) {
      classNames.push(classes.open);
    }
    return classNames.join(" ");
  }, [open]);

  return (
    <div className={containerClassName} key={talkId}>
      {TalkSheetAction}
      <div className={classes.cycleActions}>
        <Collapse in={open}>
          <div>
            {DSVFeedbackAction}
            {ParticipantAction}
            {PeerSelectionAction}
            {PeerSummaryAction}
            {AnonymousSummaryAction}
          </div>
        </Collapse>
      </div>
    </div>
  );
};

export default React.memo(EmployeeCardTalk);
